import React from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ModuleScopeProvider, useModuleScope } from "../contexts/ModuleScopeContext";
import { useHasPermission } from "../contexts/PermissionsContext";
import useModules from "../hooks/useModules";
import { ROLES } from "../lib/rbac";
import HeaderTools from "./HeaderTools";
import Breadcrumbs from "./Breadcrumbs";
import UserMenu from "./UserMenu";
import AuthedShellChrome from "./AuthedShellChrome";

function initials(name = "") {
    return name.split(" ").filter(Boolean).map((s) => s[0]?.toUpperCase()).slice(0, 2).join("");
}

function moduleNavItems(code) {
    const base = `/m/${code}`;
    // Iter60.p48 — `perm` keys are the primary nav-visibility gate.
    // `NavItemGated` calls `useHasPermission(perm)` which resolves via
    // the current user's ROLE_DEFAULTS. Adding a key here hides the nav
    // row for any role whose ROLE_DEFAULTS omit that key (e.g. the new
    // Attendance Officer is blocked from contracts / wage-rates /
    // payroll / funding / finance / reports because their catalog set
    // excludes those keys).
    // Iter60.p50 — `hideForRoles` is a secondary gate for nav items
    // that don't map cleanly to a single permission (e.g. Module
    // Dashboard). The Attendance Officer's post-login landing is
    // `/m/cn/attendance` and they must not see the Dashboard, Staff
    // Directory, or Biometric Devices rows in the CN workspace nav.
    return [
        { key: "dash", label: "Module Dashboard", path: base, hideForRoles: [ROLES.ASL_ATTENDANCE_OFFICER] },
        { key: "staff", label: "Staff", path: `${base}/staff`, perm: "employees.view" },
        { key: "attendance", label: "Attendance", path: `${base}/attendance` },
        { key: "exceptions", label: "Exceptions", path: `${base}/attendance/exceptions` },
        // Shift Rules & Holidays — Att Officer sees but write buttons
        // are hidden on the page (see ShiftRules.jsx canEditShiftRules).
        { key: "shift-rules", label: "Shift Rules & Holidays", path: `${base}/shift-rules` },
        { key: "projects", label: "Projects", path: `${base}/projects` },
        // Iter60.p50 — Biometric Devices REMOVED for Attendance Officer.
        // Iter85 — The original `perm: "biometric_devices.view"` gate
        // pointed at a permission key that does not exist in the
        // catalog, so the entry was hidden for every role. Replace
        // with `hideForRoles` to preserve the original intent (hide
        // from Attendance Officer only) without a broken permission
        // reference.
        { key: "biometric", label: "Biometric Devices", path: `${base}/biometric-devices`,
          hideForRoles: [ROLES.ASL_ATTENDANCE_OFFICER] },
        // Iter85 — Biometric Exceptions: 3-tab view (Devices health,
        // Events, HR Exceptions backfill). Different page from the
        // Hikvision device admin above. Attendance Officer CAN see
        // this one (Exceptions tab is view-only for them).
        { key: "biometric-ops", label: "Biometric Exceptions", path: `${base}/biometric` },
        { key: "contracts", label: "Contracts", path: `${base}/contracts`, perm: "contracts.view" },
        { key: "ppe", label: "Clothing & PPE", path: `${base}/ppe`, perm: "ppe.read" },
        { key: "wage-rates", label: "Wage Rates", path: `${base}/wage-rates`, perm: "wage_rates.view" },
        { key: "payroll", label: "Payroll", path: `${base}/payroll`, perm: "payroll.view" },
        { key: "funding", label: "Payroll Funding", path: `${base}/payroll/funding`, perm: "payroll_funding.write" },
        { key: "finance", label: "Module Finance", path: `${base}/finance`, perm: "finance.view_module" },
        { key: "leave", label: "Leave & Permissions", path: `${base}/leave`, perm: "leave.view" },
        { key: "discipline", label: "Discipline", path: `${base}/discipline` },
        // Iter60.p55a — Incident / Emergency directory. Officer role
        // does not carry `incidents.view` in ROLE_DEFAULTS so the nav
        // row hides for them via `NavItemGated`.
        { key: "incidents", label: "Incidents & Emergencies", path: `${base}/incidents`, perm: "incidents.view" },
        { key: "tasks", label: "Tasks", path: `${base}/tasks` },
        { key: "reports", label: "Reports", path: `${base}/reports`, perm: "reports.view" },
    ];
}

export default function ModuleAppShell({ title, children }) {
    const { moduleCode } = useParams();
    return (
        <ModuleScopeProvider moduleCode={moduleCode}>
            <Inner title={title}>{children}</Inner>
        </ModuleScopeProvider>
    );
}

function Inner({ title, children }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const scope = useModuleScope();
    const { modules, loading } = useModules();

    // Dept-mgr gate: must be their module
    if (user?.role === ROLES.ASL_DEPT_MGR && scope && user.module_scope !== scope.moduleId) {
        return <Forbidden />;
    }
    // Iter60.p50 → p50.2 — Attendance Officer is scoped to a single
    // module (Construction). Original gate compared UUIDs strictly;
    // production data drift (Officer's `module_scope` UUID didn't
    // match CN's module id) caused a dead-end 403. Widened gate:
    // allow entry when EITHER the UUID matches OR the module code
    // matches `module_scope_code` (the seed's authoritative pointer).
    // Falls back to Forbidden if neither aligns.
    if (
        user?.role === ROLES.ASL_ATTENDANCE_OFFICER
        && scope
        && user.module_scope !== scope.moduleId
        && (user.module_scope_code || "").toUpperCase() !== (scope.moduleCode || "").toUpperCase()
    ) {
        return <Forbidden />;
    }
    if (!scope) {
        if (loading) {
            return <div className="p-10 text-sm" data-testid="module-loading">Loading module…</div>;
        }
        return <Forbidden message="Unknown module" />;
    }

    const nav = moduleNavItems(scope.moduleCode.toLowerCase());

    return (
        <div className="min-h-screen flex" data-testid="module-shell">
            <aside className="asl-sidebar" data-testid="module-sidebar">
                <button
                    onClick={() => navigate("/")}
                    className="flex items-center gap-3 px-5 py-5 w-full hover:bg-white/5"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
                    data-testid="sidebar-home"
                >
                    <img src="/asl-logo.png" alt="ASL" style={{ height: 36, width: "auto" }} className="asl-logo--washed" />
                    <div className="leading-tight text-left">
                        <div className="text-white font-bold tracking-wide text-base">{scope.module.name}</div>
                        <div className="text-[11px] text-[#a8b3cc]">← Back to Home</div>
                    </div>
                </button>

                <nav className="flex-1 overflow-y-auto py-3" data-testid="module-nav">
                    <div className="asl-nav-section">{scope.module.code} Workspace</div>
                    {nav.map((item) => (
                        <NavItemGated key={item.key} item={item} moduleId={scope.moduleId} />
                    ))}
                </nav>

                <div className="px-5 py-4 text-[11px] leading-snug"
                    style={{ borderTop: "1px solid rgba(255,255,255,0.08)", color: "#8a96b3" }}>
                    Managed by Kaafi Hire Employment Services Ltd
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header className="bg-white px-8 py-4 flex items-center justify-between"
                    style={{ borderBottom: "1px solid var(--asl-border)" }} data-testid="module-header">
                    <h1 className="text-xl font-bold asl-navy-text" data-testid="page-title">{title}</h1>
                    <div className="flex items-center gap-3">
                        <HeaderTools />
                        <UserMenu />
                    </div>
                </header>
                <Breadcrumbs />
                <main className="flex-1 p-8 overflow-auto">{children}</main>
            </div>
            <AuthedShellChrome />
        </div>
    );
}

function NavItemGated({ item, moduleId }) {
    const { user } = useAuth();
    // Hook must always run; when no perm is required we treat it as allowed.
    const allowed = useHasPermission(item.perm || "", moduleId);
    if (item.perm && !allowed) return null;
    // Iter60.p50 — Secondary role-based hide for items that don't map
    // to a single permission key (e.g. Module Dashboard for the
    // Attendance Officer).
    if (item.hideForRoles && user?.role && item.hideForRoles.includes(user.role)) return null;
    return (
        <NavLink
            to={item.path}
            end={item.key === "dash"}
            className={({ isActive }) => `asl-nav-item ${isActive ? "active" : ""}`}
            data-testid={`mnav-${item.key}`}
        >
            <span>{item.label}</span>
        </NavLink>
    );
}

function Forbidden({ message = "You don't have access to this module." }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-10" data-testid="forbidden-403">
            <div className="asl-card p-10 max-w-md text-center space-y-4">
                <div className="text-7xl font-bold asl-crimson-text">403</div>
                <h2 className="text-xl font-semibold asl-navy-text">{message}</h2>
                <a href="/" className="inline-block underline asl-navy-text">← Back to Home</a>
            </div>
        </div>
    );
}
